import React, { useRef } from 'react';
import { Colors } from '../../../data/Colors';
import { Group, Vector3 } from 'three';
import useSimpleTransition from '../hooks/useSimpleTransition';
import useLerpedVector3 from '../hooks/useLerpedVector3';
import SimpleVector from '../spheres/SimpleVector';
import OriginSphere from '../spheres/OriginSphere';
import LocationSphere from '../spheres/LocationSphere';
import useAspectScale from '../hooks/useAspectScale';
import { DetailSectionIDs } from '../../../data/Sections';
import SimpleSphere from '../spheres/SimpleSphere';
import VectorLabel from '../spheres/VectorLabel';
import useAlignLabel from '../hooks/useAlignLabel';
import { useDetailViewContext } from '../../../context/DetailViewContext';
import FocusPoints from '../../../data/FocusPoints';
import {SceneSection} from '../View3D';

const homeSphereSize = 0.9;
const Positions = {
  husband: {
    vector: new Vector3(-0.45, 0.1, 0.4),
    sphere: new Vector3(-0.55, 0, 0.4)
  },
  cat: {
    vector: new Vector3(-0.3, -0.15, -0.4),
    sphere: new Vector3(-0.3, 0.0, 0)
  },
  wife: {
    vector: new Vector3(0.5, 0.0, 0.1),
    sphere: new Vector3(0.5, 0.0, -0.1)
  },
  home: {
    vector: new Vector3(0.0, 0.5, 0),
    sphere: new Vector3(0, 0.0, 0)
  }
};

const origin = new Vector3(0, -.7, 0);

export default function VectorRepresentation({ groupProps, texts }: SceneSection): JSX.Element
{
  const toSphereTransition = useSimpleTransition(DetailSectionIDs.DecodingVector, DetailSectionIDs.DecodingSphere);
  const scale = useAspectScale(1.0);

  const husbandPos = useLerpedVector3(Positions.husband.vector, Positions.husband.sphere, toSphereTransition);
  const catPos = useLerpedVector3(Positions.cat.vector, Positions.cat.sphere, toSphereTransition);
  const wifePos = useLerpedVector3(Positions.wife.vector, Positions.wife.sphere, toSphereTransition);
  const homePos = useLerpedVector3(Positions.home.vector, Positions.home.sphere, toSphereTransition);

  const labelRef = useRef<Group>(null);

  useAlignLabel(labelRef, homePos, homeSphereSize * toSphereTransition);

  const {activeDetailID, activeFocusID} = useDetailViewContext();

  return (
    <group 
      {...groupProps} 
      scale={[ scale, scale, scale ]} 
      visible={
        activeFocusID === FocusPoints.None 
        || activeDetailID === DetailSectionIDs.DecodingVector
      }>
      <OriginSphere position={origin}/>

      <SimpleSphere 
        label={texts.wife} 
        arrowOrigin={origin} 
        color={Colors.WifeVector}
        sphereTransition={toSphereTransition} 
        sphereCenter={wifePos} 
        flipSide={-1.2}
        sphereSize={0.15}
      />
      <SimpleSphere
        label={texts.husband}
        arrowOrigin={origin}
        color={Colors.HusbandVector}
        sphereTransition={toSphereTransition} sphereCenter={husbandPos}
        sphereSize={0.15} flipSide={0.9}
      />
      <SimpleSphere
        label={texts.cat}
        arrowOrigin={origin}
        color={Colors.CatVector}
        sphereTransition={toSphereTransition} sphereCenter={catPos}
        sphereSize={0.125} flipSide={-1.6}
      />

      <LocationSphere
        sphereSize={homeSphereSize}
        sphereCenter={homePos}
        arrowOrigin={origin} hideArrow
        sphereTransition={toSphereTransition}
      />

      <VectorLabel
        fontSize={15}
        bgColor={Colors.LocationVector}
        textColor={0xffffff}
        text={texts.home}
        height={0.07}
        ref={labelRef} bgAlpha={1.0}
        font="Nexa"
      />

      {/* "Inside" vectors */}
      <SimpleVector
        position={husbandPos}
        target={homePos}
        color={Colors.NeutralGrey}
        hideDot
        visible={toSphereTransition < .1} 
        label={texts.inside} 
        alignLabel
      />
      <SimpleVector
        position={catPos}
        target={homePos}
        color={Colors.NeutralGrey} hideDot
        visible={toSphereTransition < .1} 
        label={texts.inside} 
        alignLabel 
      />
      <SimpleVector
        position={wifePos}
        target={homePos}
        color={Colors.NeutralGrey} 
        hideDot
        visible={toSphereTransition < .1}
        label={texts.inside}
        alignLabel
        flipSide={true}
      />
    </group>
  );
}
